import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { db } from './firebase';
import { sessionStart } from './dateUtils';
import { SESSION_MS } from './sessionActions';
import { displayName } from './names';

// Study progress (brief 026): a private list of the user's finished sessions.
// Entries are worked out from `sessions` (accepted, slot over); only the owner's
// title edits and deletions are stored, in users/{uid}/progress/{sessionId}.

export const TITLE_MAX = 120;
const HOURS_EACH = SESSION_MS / 3600000;

// "Session with Ana"; the subjects show as chips under it (review R4).
export const defaultTitle = partner => `Session with ${partner}`;

// sessions: [{ id, ...session, isMine }]; overrides: { [sessionId]: { title, deleted } }.
export function progressEntries(sessions, overrides = {}, now = Date.now()) {
  const seen = new Set();
  return sessions
    .filter(s => {
      if (s.status !== 'accepted' || seen.has(s.id)) return false;
      seen.add(s.id);
      const t = sessionStart(s)?.getTime();
      return t != null && t + SESSION_MS <= now && !overrides[s.id]?.deleted;
    })
    .map(s => {
      const subjects = s.subjects || [];
      const title = (overrides[s.id]?.title || '').trim();
      const partner = displayName(s.isMine ? s.toName : s.fromName, 'Study partner');
      return {
        id: s.id,
        start: sessionStart(s),
        partner,
        subjects,
        title: title || defaultTitle(partner),
        edited: !!title,
      };
    })
    .sort((a, b) => b.start - a.start);
}

// Sessions, hours (two each) and the three most-studied subjects.
export function progressTotals(entries) {
  const count = new Map();
  entries.forEach(e => e.subjects.forEach(s => count.set(s, (count.get(s) || 0) + 1)));
  const topSubjects = [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 3).map(([s]) => s);
  return { sessions: entries.length, hours: entries.length * HOURS_EACH, topSubjects };
}

// [{ month: 'November 2026', entries }], newest first, in the viewer's zone.
export function groupByMonth(entries, zone) {
  const groups = [];
  entries.forEach(e => {
    const month = DateTime.fromJSDate(e.start, { zone: zone || undefined }).toFormat('LLLL yyyy');
    const last = groups[groups.length - 1];
    if (last && last.month === month) last.entries.push(e);
    else groups.push({ month, entries: [e] });
  });
  return groups;
}

// Live entries for this user: both directions of sessions plus their overrides.
export function useProgress(uid) {
  const [sent, setSent] = useState(null);
  const [received, setReceived] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!uid) return undefined;
    const listen = (field, isMine, set) => onSnapshot(
      query(collection(db, 'sessions'), where(field, '==', uid)),
      snap => set(snap.docs.map(d => ({ id: d.id, ...d.data(), isMine }))),
      e => { console.error(e); set([]); },
    );
    const u1 = listen('fromUid', true, setSent);
    const u2 = listen('toUid', false, setReceived);
    const u3 = onSnapshot(collection(db, 'users', uid, 'progress'),
      snap => setOverrides(Object.fromEntries(snap.docs.map(d => [d.id, d.data()]))),
      e => { console.error(e); setOverrides({}); });
    // A session that ends while the screen is open joins the list.
    const tick = setInterval(() => setNow(Date.now()), 60000);
    return () => { u1(); u2(); u3(); clearInterval(tick); };
  }, [uid]);

  if (!sent || !received || !overrides) return null;
  return progressEntries([...sent, ...received], overrides, now);
}

export const saveProgressTitle = (uid, sessionId, title) =>
  setDoc(doc(db, 'users', uid, 'progress', sessionId), { title: title.trim().slice(0, TITLE_MAX), updatedAt: serverTimestamp() }, { merge: true });

export const deleteProgressEntry = (uid, sessionId) =>
  setDoc(doc(db, 'users', uid, 'progress', sessionId), { deleted: true, updatedAt: serverTimestamp() }, { merge: true });

// Account deletion.
export async function deleteAllProgress(uid) {
  const snap = await getDocs(collection(db, 'users', uid, 'progress'));
  await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
}
